import { capitalizeFirstLetter } from './string';
import i18n from '../i18n';

// Overview mode reduces a song to its skeleton of unique, primary parts. Beyond duplicates
// (repeats of an original), standalone Notes and Hidden parts are also set aside — they're
// structural noise when you just want to scan the song's real sections.
export const isHiddenInOverview = (part) =>
  Boolean(part.isDuplicate || part.isHidden || part.name === 'Note');

// The overview indicator reports how much of the song is being withheld, so the ratio has to be
// derived from the same predicate the body filters with — otherwise the two drift apart.
export const countOverviewParts = (parts = []) => ({
  shown: parts.filter((part) => !isHiddenInOverview(part)).length,
  total: parts.length,
});

// Intro, Music, Instrumental and Outro are the song's instrumental parts (the red chips). They
// often hold nothing but a line of chords, so features that key off lyrics have to make room
// for them.
export const INSTRUMENTAL_PARTS = ['Intro', 'Music', 'Instrumental', 'Outro'];

export const isInstrumentalPart = (partName) => INSTRUMENTAL_PARTS.includes(partName);

// Parts whose body is the words the team sings. There is no room in them for a grid, so a chart for
// one is stored on the part itself instead of written into its lyrics. Section, Note and Hidden are
// left out for the opposite reason — they have no lyrics for a chart to go with.
export const CHARTABLE_PARTS = ['Verse', 'Pre-chorus', 'Chorus', 'Bridge', 'Tag', 'Refrain'];

export const canAttachChart = (partName) => CHARTABLE_PARTS.includes(partName);

// The parts the chord bars builder is offered on: the instrumentals, and the parts made of words
export const canChart = (partName) => isInstrumentalPart(partName) || canAttachChart(partName);

// A part has something to save once it has words, or a chart standing in for them
export const hasPartContent = (lyrics, partName, hasChart) =>
  Boolean(lyrics?.trim()) || (canChart(partName) && Boolean(hasChart));

// Solos go last as one label, so "(Solo)" is said once however many there are
const withSolosLast = (items, isSolo, label, joiner) => {
  const solos = items.filter(isSolo).map(label);
  const rest = items.filter((item) => !isSolo(item)).map(label);
  return solos.length ? [...rest, `${solos.join(joiner)} (Solo)`] : rest;
};

// Who a part is assigned to, as the pills show it and the callout says it
export const getPeopleLabels = (part) =>
  withSolosLast(
    (part?.assignedUsers ?? []).filter((u) => u?.name),
    (u) => u.solo,
    (u) => capitalizeFirstLetter(u.name),
    ' & ',
  );

// An instrument of instruments.json in the app's language. Parts store the English name, which
// the solo and drum checks match on, so only what's shown is translated.
export const bandInstrumentKey = (name) =>
  `bandInstruments.${name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

export const getBandInstrumentLabel = (name) => i18n.t(bandInstrumentKey(name), { defaultValue: name });

export const getInstrumentLabels = (part) =>
  withSolosLast(
    part?.assignedInstruments ?? [],
    (i) => (part.soloInstruments ?? []).includes(i),
    getBandInstrumentLabel,
    ' + ',
  );

// The instruments a solo on which is a drum solo: playing through, the part is the drums alone
export const DRUM_SOLO_INSTRUMENTS = ['Drums', 'Percussion', 'Cajón'];

export const isDrumSolo = (part) => (part?.soloInstruments ?? []).some((i) => DRUM_SOLO_INSTRUMENTS.includes(i));

const isPartAssignedTo = (part, userId) => Boolean(part.assignedUsers?.some((u) => u._id === userId));

/**
 * The user's parts in the song, each once, as `{ id, name, number }` — a repeat goes by its original's
 * name and number, and by where the user first comes to it. The same list the server works out for a
 * song in the team list, which carries no parts but `myParts`; a song swapped in from a save has its
 * parts, and they are the fresher of the two.
 */
export const getMyParts = (song, userId) => {
  if (!Array.isArray(song?.parts)) return song?.myParts ?? [];

  const listed = new Map();

  song.parts.forEach((part) => {
    if (!isPartAssignedTo(part, userId)) return;

    const original = part.isDuplicate ? song.parts.find((p) => p.id === part.duplicateId) : part;

    if (original && !listed.has(original.id)) {
      listed.set(original.id, { id: part.id, name: original.name, number: original.number });
    }
  });

  return [...listed.values()];
};

// Whether the user has a part in the song
export const isSongAssignedTo = (song, userId) => getMyParts(song, userId).length > 0;

// The song's parts the user is on, every one of them, in the order they come
export const getAssignedPartIds = (song, userId) =>
  (song?.parts ?? []).filter((part) => isPartAssignedTo(part, userId)).map((part) => part.id);

/**
 * Where a part's MIDI belongs in a chart that is written for more than one instrument.
 *
 * A clip is tagged on upload with the instrument it was played for (`midi.playsWith`), because a
 * melody played over the trumpet's chords clashes under the guitar's when the two charts differ.
 * An untagged clip — every clip saved before the field existed — plays with all of them, and so
 * does one tagged with an instrument the part no longer carries: a stale tag should leave the
 * melody where it was rather than making it vanish from every chart.
 *
 * @param {object} part - The part the clip hangs off
 * @param {string[]} sectionInstruments - The instrument each of the chart's sections belongs to,
 *   in order; a chart written for one instrument is a single entry
 * @returns {{ plays: boolean, section: number }} `section` is which of them the clip belongs to,
 *   or -1 when it isn't tied to one and plays from the top
 */
export const getMelodySection = (part, sectionInstruments = []) => {
  const playsWith = part?.midi?.playsWith;

  // A chart that names no instrument at all — one combined chart, the usual case — is the whole
  // part, so the clip plays under it whatever it is tagged with
  if (!playsWith || sectionInstruments.length === 0 || !(part?.assignedInstruments || []).includes(playsWith)) {
    return { plays: true, section: -1 };
  }

  const section = sectionInstruments.indexOf(playsWith);

  return { plays: section >= 0, section };
};
